// Phase SA-5b/2 — Pure helpers for slip image upload.
//
// File validation + storage-path generation + display utilities.
// No side effects — every function is testable without a real
// File / Blob (we use the standard File type at the call sites).

/** Same set as the migration CHECK constraints + bucket allowed
 * MIME types. Keep in sync with
 * supabase/migrations/20260512120000_sa5b_slip_image_upload.sql. */
export const ALLOWED_MIMES = [
  "image/jpeg",
  "image/png",
  "image/heic",
  "image/heif",
  "image/webp",
  "application/pdf",
] as const;

export type AllowedMime = (typeof ALLOWED_MIMES)[number];

/** 12 MB — matches the Storage bucket file_size_limit and the
 * slip_image_size_bytes CHECK constraint. */
export const MAX_BYTES = 12 * 1024 * 1024;

/** Pure: validate a file. Returns null for valid; a user-facing
 * hint string for any rejection. */
export function validateFile(file: File): string | null {
  if (!ALLOWED_MIMES.includes(file.type as AllowedMime)) {
    return "Unsupported file type. Use JPG, PNG, HEIC, WEBP, or PDF.";
  }
  if (file.size <= 0) {
    return "This file appears empty.";
  }
  if (file.size > MAX_BYTES) {
    return `File is too large (${humanSize(file.size)}). Maximum 12 MB.`;
  }
  return null;
}

/** Pure: derive a file extension from a MIME type. Keeps storage
 * paths predictable (helps with retention / debugging). */
export function mimeToExt(mime: string): string {
  switch (mime) {
    case "image/jpeg":
      return "jpg";
    case "image/png":
      return "png";
    case "image/heic":
      return "heic";
    case "image/heif":
      return "heif";
    case "image/webp":
      return "webp";
    case "application/pdf":
      return "pdf";
    default:
      return "bin";
  }
}

/** Pure: human file size. "1.2 MB" / "320 KB" / "640 B". */
export function humanSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return "—";
  if (bytes < 1024) return `${Math.round(bytes)} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(kb >= 10 ? 0 : 1)} KB`;
  const mb = kb / 1024;
  return `${mb.toFixed(mb >= 10 ? 0 : 1)} MB`;
}

/** Pure: storage path. Format: `{userId}/{YYYYMMDD}_{rand}.{ext}`.
 *
 * The path's first segment is the user_id so storage RLS works
 * (see SA-5b/1 migration policy: split_part(name, '/', 1)).
 *
 * `now` and `rand` are injectable for deterministic tests. */
export function generatePath(
  userId: string,
  mime: string,
  now: Date = new Date(),
  rand: string = randomToken(),
): string {
  const y = now.getUTCFullYear();
  const m = String(now.getUTCMonth() + 1).padStart(2, "0");
  const d = String(now.getUTCDate()).padStart(2, "0");
  const ext = mimeToExt(mime);
  return `${userId}/${y}${m}${d}_${rand}.${ext}`;
}

/** Pure: short URL-safe random token. Uses crypto.randomUUID when
 * available; falls back to Math.random for jsdom-light envs. */
export function randomToken(): string {
  if (typeof globalThis.crypto?.randomUUID === "function") {
    return globalThis.crypto.randomUUID().slice(0, 8);
  }
  return Math.random().toString(36).slice(2, 10);
}

/** Pure: is a MIME type one of the image types (vs PDF)?
 * Drives the preview-thumbnail vs PDF-badge branch in the
 * dropzone. */
export function isImageMime(mime: string): boolean {
  return mime.startsWith("image/");
}
